import { track } from 'client/analytics/helper'
import { AnalyticsPayloadInterface, checkPlacement } from 'client/Global/Analytics'
import { trackingFields } from 'client/Global/AnalyticsEvents'
import { SubscriptionInterface } from 'client/Interfaces/Subscription'
import { l } from 'client/Other/Translation'
import * as React from 'react'

import Button, { ButtonType } from '../../common/Button/Button'
import { IIntermediaryContext } from '../PremiumFeatureIntermediaryModal'

export interface IntermediaryConfigInterface {
  header: JSX.Element
  body: JSX.Element
  footerButtons: {
    cancel: string
    confirm: string
    automations: {
      cancel: string
      confirm: string
    }
    analytics?: {
      cancel?: {
        name: string
        payload?: AnalyticsPayloadInterface
      }
      confirm?: {
        name: string
        payload?: AnalyticsPayloadInterface
      }
    }
  }
}

export interface IntermediaryConfigProps {
  className: string
  subscription?: SubscriptionInterface
  context?: IIntermediaryContext
}

const localizationKeys = {
  intermediary: {
    paper: {
      header: 'intermediary.paper.header',
      title: 'intermediary.paper.title',
      info: 'intermediary.paper.info',
      cancel: 'intermediary.paper.cancel',
      confirm: 'intermediary.paper.confirm',
      referral_bullets: [
        {
          label: 'intermediary.paper.bullet.label_1',
          subLabel: 'intermediary.paper.bullet.sub_label_1',
        },
        {
          label: 'intermediary.paper.bullet.label_2',
          subLabel: 'intermediary.paper.bullet.sub_label_2',
        },
        {
          label: 'intermediary.paper.bullet.label_3',
          subLabel: 'intermediary.paper.bullet.sub_label_3',
        },
        {
          label: 'intermediary.paper.referral_bullet.label_4',
          subLabel: 'intermediary.paper.referral_bullet.sub_label_4',
        },
      ],
      bullets: [
        {
          label: 'intermediary.paper.bullet.label_1',
          subLabel: 'intermediary.paper.bullet.sub_label_1',
        },
        {
          label: 'intermediary.paper.bullet.label_2',
          subLabel: 'intermediary.paper.bullet.sub_label_2',
        },
        {
          label: 'intermediary.paper.bullet.label_3',
          subLabel: 'intermediary.paper.bullet.sub_label_3',
        },
        {
          label: 'intermediary.paper.bullet.label_4',
          subLabel: 'intermediary.paper.bullet.sub_label_4',
        },
      ],
    },
    einvoice: {
      header: 'intermediary.einvoice.header',
      title: 'intermediary.einvoice.title',
      info: 'intermediary.einvoice.info',
      cancel: 'intermediary.einvoice.cancel',
      confirm: 'intermediary.einvoice.confirm',
      referral_bullets: [
        {
          label: 'intermediary.einvoice.bullet.label_1',
          subLabel: 'intermediary.einvoice.bullet.sub_label_1',
        },
        {
          label: 'intermediary.einvoice.bullet.label_2',
          subLabel: 'intermediary.einvoice.bullet.sub_label_2',
        },
        {
          label: 'intermediary.einvoice.bullet.label_3',
          subLabel: 'intermediary.einvoice.bullet.sub_label_3',
        },
        {
          label: 'intermediary.einvoice.referral_bullet.label_4',
          subLabel: 'intermediary.einvoice.referral_bullet.sub_label_4',
        },
      ],
      bullets: [
        {
          label: 'intermediary.einvoice.bullet.label_1',
          subLabel: 'intermediary.einvoice.bullet.sub_label_1',
        },
        {
          label: 'intermediary.einvoice.bullet.label_2',
          subLabel: 'intermediary.einvoice.bullet.sub_label_2',
        },
        {
          label: 'intermediary.einvoice.bullet.label_3',
          subLabel: 'intermediary.einvoice.bullet.sub_label_3',
        },
        {
          label: 'intermediary.einvoice.bullet.label_4',
          subLabel: 'intermediary.einvoice.bullet.sub_label_4',
        },
      ],
    },
    reminder: {
      header: 'intermediary.reminder.header',
      title: 'intermediary.reminder.title',
      info: 'intermediary.reminder.info',
      cancel: 'intermediary.reminder.cancel',
      confirm: 'intermediary.reminder.confirm',
      referral_bullets: [
        {
          label: 'intermediary.reminder.bullet.label_1',
          subLabel: 'intermediary.reminder.bullet.sub_label_1',
        },
        {
          label: 'intermediary.reminder.bullet.label_2',
          subLabel: 'intermediary.reminder.bullet.sub_label_2',
        },
        {
          label: 'intermediary.reminder.referral_bullet.label_3',
          subLabel: 'intermediary.reminder.referral_bullet.sub_label_3',
        },
      ],
      bullets: [
        {
          label: 'intermediary.reminder.bullet.label_1',
          subLabel: 'intermediary.reminder.bullet.sub_label_1',
        },
        {
          label: 'intermediary.reminder.bullet.label_2',
          subLabel: 'intermediary.reminder.bullet.sub_label_2',
        },
        {
          label: 'intermediary.reminder.bullet.label_3',
          subLabel: 'intermediary.reminder.bullet.sub_label_3',
        },
      ],
    },
    reminderByPost: {
      header: 'intermediary.reminder_post.header',
      title: 'intermediary.reminder_post.title',
      info: 'intermediary.reminder_post.info',
      cancel: 'intermediary.reminder_post.cancel',
      confirm: 'intermediary.reminder_post.confirm',
      referral_bullets: [
        {
          label: 'intermediary.reminder_post.bullet.label_1',
          subLabel: 'intermediary.reminder_post.bullet.sub_label_1',
        },
        {
          label: 'intermediary.reminder_post.bullet.label_2',
          subLabel: 'intermediary.reminder_post.bullet.sub_label_2',
        },
        {
          label: 'intermediary.reminder_post.referral_bullet.label_3',
          subLabel: 'intermediary.reminder_post.referral_bullet.sub_label_3',
        },
      ],
      bullets: [
        {
          label: 'intermediary.reminder_post.bullet.label_1',
          subLabel: 'intermediary.reminder_post.bullet.sub_label_1',
        },
        {
          label: 'intermediary.reminder_post.bullet.label_2',
          subLabel: 'intermediary.reminder_post.bullet.sub_label_2',
        },
        {
          label: 'intermediary.reminder_post.bullet.label_3',
          subLabel: 'intermediary.reminder_post.bullet.sub_label_3',
        },
      ],
    },

    tracker: {
      header: 'intermediary.tracker.header',
      title: 'intermediary.tracker.title',
      info: 'intermediary.tracker.info',
      cancel: 'intermediary.tracker.go_back',
      confirm: 'intermediary.tracker.confirm',
      referral_bullets: [
        {
          label: 'intermediary.tracker.bullet.label_1',
          subLabel: 'intermediary.tracker.bullet.sub_label_1',
        },
        {
          label: 'intermediary.tracker.bullet.label_2',
          subLabel: 'intermediary.tracker.bullet.sub_label_2',
        },
        {
          label: 'intermediary.tracker.referral_bullet.label_3',
          subLabel: 'intermediary.tracker.referral_bullet.sub_label_3',
        },
      ],
      bullets: [
        {
          label: 'intermediary.tracker.bullet.label_1',
          subLabel: 'intermediary.tracker.bullet.sub_label_1',
        },
        {
          label: 'intermediary.tracker.bullet.label_2',
          subLabel: 'intermediary.tracker.bullet.sub_label_2',
        },
        {
          label: 'intermediary.tracker.bullet.label_3',
          subLabel: 'intermediary.tracker.bullet.sub_label_3',
        },
      ],
    },
    estimateToInvoice: {
      header: 'intermediary.estimateToInvoice.header',
      title: 'intermediary.estimateToInvoice.title',
      info: 'intermediary.estimateToInvoice.info',
      cancel: 'intermediary.estimateToInvoice.go_back',
      confirm: 'intermediary.estimateToInvoice.confirm',
      referral_bullets: [
        {
          label: 'intermediary.estimateToInvoice.bullet.label_1',
          subLabel: 'intermediary.estimateToInvoice.bullet.sub_label_1',
        },
        {
          label: 'intermediary.estimateToInvoice.referral_bullet.label_2',
          subLabel: 'intermediary.estimateToInvoice.referral_bullet.sub_label_2',
        },
      ],
      bullets: [
        {
          label: 'intermediary.estimateToInvoice.bullet.label_1',
          subLabel: 'intermediary.estimateToInvoice.bullet.sub_label_1',
        },
        {
          label: 'intermediary.estimateToInvoice.bullet.label_2',
          subLabel: 'intermediary.estimateToInvoice.bullet.sub_label_2',
        },
      ],
    },
    customer: {
      header: 'intermediary.customer.header',
      title: 'intermediary.customer.title',
      info: 'intermediary.customer.info',
      cancel: 'intermediary.customer.cancel',
      confirm: 'intermediary.customer.confirm',
      referral_bullets: [
        {
          label: 'intermediary.customer.bullet.label_1',
          subLabel: 'intermediary.customer.bullet.sub_label_1',
        },
        {
          label: 'intermediary.customer.referral_bullet.label_2',
          subLabel: 'intermediary.customer.referral_bullet.sub_label_2',
        },
      ],
      bullets: [
        {
          label: 'intermediary.customer.bullet.label_1',
          subLabel: 'intermediary.customer.bullet.sub_label_1',
        },
        {
          label: 'intermediary.customer.bullet.label_2',
          subLabel: 'intermediary.customer.bullet.sub_label_2',
        },
      ],
    },
    tradeNames: {
      header: 'intermediary.tradenames.header',
      title: 'intermediary.tradenames.title',
      info: 'intermediary.tradenames.info',
      cancel: 'intermediary.tradenames.go_back',
      confirm: 'intermediary.tradenames.confirm',
      referral_bullets: [
        {
          label: 'intermediary.tradenames.bullet.label_1',
          subLabel: 'intermediary.tradenames.bullet.sub_label_1',
        },
        {
          label: 'intermediary.tradenames.referral_bullet.label_2',
          subLabel: 'intermediary.tradenames.referral_bullet.sub_label_2',
        },
      ],
      bullets: [
        {
          label: 'intermediary.tradenames.bullet.label_1',
          subLabel: 'intermediary.tradenames.bullet.sub_label_1',
        },
        {
          label: 'intermediary.tradenames.bullet.label_2',
          subLabel: 'intermediary.tradenames.bullet.sub_label_2',
        },
      ],
    },
    payment_schedule: {
      header: 'intermediary.payment_schedule.header',
      title: 'intermediary.payment_schedule.title',
      info: 'intermediary.payment_schedule.info',
      cancel: 'intermediary.payment_schedule.cancel',
      confirm: 'intermediary.payment_schedule.confirm',
      referral_bullets: [
        {
          label: 'intermediary.payment_schedule.bullet.label_1',
          subLabel: 'intermediary.payment_schedule.bullet.sub_label_1',
        },
        {
          label: 'intermediary.payment_schedule.bullet.label_2',
          subLabel: 'intermediary.payment_schedule.bullet.sub_label_2',
        },
        {
          label: 'intermediary.payment_schedule.bullet.label_3',
          subLabel: 'intermediary.payment_schedule.bullet.sub_label_3',
        },
        {
          label: 'intermediary.payment_schedule.referral_bullet.label_4',
          subLabel: 'intermediary.payment_schedule.referral_bullet.sub_label_4',
        },
      ],
      bullets: [
        {
          label: 'intermediary.payment_schedule.bullet.label_1',
          subLabel: 'intermediary.payment_schedule.bullet.sub_label_1',
        },
        {
          label: 'intermediary.payment_schedule.bullet.label_2',
          subLabel: 'intermediary.payment_schedule.bullet.sub_label_2',
        },
        {
          label: 'intermediary.payment_schedule.bullet.label_3',
          subLabel: 'intermediary.payment_schedule.bullet.sub_label_3',
        },
        {
          label: 'intermediary.payment_schedule.bullet.label_4',
          subLabel: 'intermediary.payment_schedule.bullet.sub_label_4',
        },
      ],
    },
    emailAttachment: {
      header: 'intermediary.emailAttachment.header',
      title: 'intermediary.emailAttachment.title',
      info: 'intermediary.emailAttachment.info',
      cancel: 'intermediary.emailAttachment.cancel',
      confirm: 'intermediary.emailAttachment.confirm',
      referral_bullets: [
        {
          label: 'intermediary.emailAttachment.bullet.label_1',
          subLabel: 'intermediary.emailAttachment.bullet.sub_label_1',
        },
        {
          label: 'intermediary.emailAttachment.referral_bullet.label_2',
          subLabel: 'intermediary.emailAttachment.referral_bullet.sub_label_2',
        },
      ],
      bullets: [
        {
          label: 'intermediary.emailAttachment.bullet.label_1',
          subLabel: 'intermediary.emailAttachment.bullet.sub_label_1',
        },
        {
          label: 'intermediary.emailAttachment.bullet.label_2',
          subLabel: 'intermediary.emailAttachment.bullet.sub_label_2',
        },
      ],
    },
    recurring_profile: {
      header: 'intermediary.recurring_profile.header',
      title: 'intermediary.recurring_profile.title',
      info: 'intermediary.recurring_profile.info',
      cancel: 'intermediary.recurring_profile.cancel',
      confirm: 'intermediary.recurring_profile.confirm',
      referral_bullets: [
        {
          label: 'intermediary.recurring_profile.bullet.label_1',
          subLabel: 'intermediary.recurring_profile.bullet.sub_label_1',
        },
        {
          label: 'intermediary.recurring_profile.bullet.label_2',
          subLabel: 'intermediary.recurring_profile.bullet.sub_label_2',
        },
        {
          label: 'intermediary.recurring_profile.bullet.label_3',
          subLabel: 'intermediary.recurring_profile.bullet.sub_label_3',
        },
        {
          label: 'intermediary.recurring_profile.referral_bullet.label_4',
          subLabel: 'intermediary.recurring_profile.referral_bullet.sub_label_4',
        },
      ],
      bullets: [
        {
          label: 'intermediary.recurring_profile.bullet.label_1',
          subLabel: 'intermediary.recurring_profile.bullet.sub_label_1',
        },
        {
          label: 'intermediary.recurring_profile.bullet.label_2',
          subLabel: 'intermediary.recurring_profile.bullet.sub_label_2',
        },
        {
          label: 'intermediary.recurring_profile.bullet.label_3',
          subLabel: 'intermediary.recurring_profile.bullet.sub_label_3',
        },
        {
          label: 'intermediary.recurring_profile.bullet.label_4',
          subLabel: 'intermediary.recurring_profile.bullet.sub_label_4',
        },
      ],
    },
    banking: {
      header: 'intermediary.banking.header',
      title: 'intermediary.banking.title',
      info: 'intermediary.banking.info',
      infoLinkLabel: 'intermediary.banking.linkLabel',
      infoLink: 'intermediary.banking.link',
      cancel: 'intermediary.banking.cancel',
      confirm: 'intermediary.banking.confirm',
      referral_bullets: [
        {
          label: 'intermediary.banking.bullet.label_1',
          subLabel: 'intermediary.banking.bullet.sub_label_1',
        },
        {
          label: 'intermediary.banking.bullet.label_2',
          subLabel: 'intermediary.banking.bullet.sub_label_2',
        },
        {
          label: 'intermediary.banking.bullet.label_3',
          subLabel: 'intermediary.banking.bullet.sub_label_3',
        },
        {
          label: 'intermediary.banking.referral_bullet.label_4',
          subLabel: 'intermediary.banking.referral_bullet.sub_label_4',
        },
      ],
      bullets: [
        {
          label: 'intermediary.banking.bullet.label_1',
          subLabel: 'intermediary.banking.bullet.sub_label_1',
        },
        {
          label: 'intermediary.banking.bullet.label_2',
          subLabel: 'intermediary.banking.bullet.sub_label_2',
        },
        {
          label: 'intermediary.banking.bullet.label_3',
          subLabel: 'intermediary.banking.bullet.sub_label_3',
        },
        {
          label: 'intermediary.banking.bullet.label_4',
          subLabel: 'intermediary.banking.bullet.sub_label_4',
        },
      ],
    },
    documentCustomisation: {
      header: 'intermediary.documentCustomisation.header',
      title: 'intermediary.documentCustomisation.title',
      info: 'intermediary.documentCustomisation.info',
      cancel: 'intermediary.documentCustomisation.cancel',
      confirm: 'intermediary.documentCustomisation.confirm',
      referral_bullets: [
        {
          label: 'intermediary.documentCustomisation.bullet.label_1',
          subLabel: 'intermediary.documentCustomisation.bullet.sub_label_1',
        },
        {
          label: 'intermediary.documentCustomisation.referral_bullet.label_2',
          subLabel: 'intermediary.documentCustomisation.referral_bullet.sub_label_2',
        },
        {
          label: 'intermediary.documentCustomisation.referral_bullet.label_3',
          subLabel: 'intermediary.documentCustomisation.referral_bullet.sub_label_3',
        },
        {
          label: 'intermediary.documentCustomisation.referral_bullet.label_4',
          subLabel: 'intermediary.documentCustomisation.referral_bullet.sub_label_4',
        },
        {
          label: 'intermediary.documentCustomisation.referral_bullet.label_5',
          subLabel: 'intermediary.documentCustomisation.referral_bullet.sub_label_5',
        },
      ],
      bullets: [
        {
          label: 'intermediary.documentCustomisation.bullet.label_1',
          subLabel: 'intermediary.documentCustomisation.bullet.sub_label_1',
        },
        {
          label: 'intermediary.documentCustomisation.bullet.label_2',
          subLabel: 'intermediary.documentCustomisation.bullet.sub_label_2',
        },
        {
          label: 'intermediary.documentCustomisation.referral_bullet.label_3',
          subLabel: 'intermediary.documentCustomisation.referral_bullet.sub_label_3',
        },
        {
          label: 'intermediary.documentCustomisation.referral_bullet.label_4',
          subLabel: 'intermediary.documentCustomisation.referral_bullet.sub_label_4',
        },
        {
          label: 'intermediary.documentCustomisation.referral_bullet.label_5',
          subLabel: 'intermediary.documentCustomisation.referral_bullet.sub_label_5',
        },
      ],
    },
    consumables: {
      header: 'intermediary.consumables.header',
      title: 'intermediary.consumables.title',
      info: 'intermediary.consumables.info',
      infoLinkLabel: 'intermediary.consumables.linkLabel',
      infoLink: 'intermediary.consumables.link',
      cancel: 'intermediary.consumables.cancel',
      confirm: 'intermediary.consumables.confirm',
      referral_bullets: [
        {
          label: 'intermediary.consumables.bullet.label_1',
          subLabel: 'intermediary.consumables.bullet.sub_label_1',
        },
        {
          label: 'intermediary.consumables.bullet.label_2',
          subLabel: 'intermediary.consumables.bullet.sub_label_2',
        },
        {
          label: 'intermediary.consumables.bullet.label_3',
          subLabel: 'intermediary.consumables.bullet.sub_label_3',
        },
      ],
      bullets: [
        {
          label: 'intermediary.consumables.bullet.label_1',
          subLabel: 'intermediary.consumables.bullet.sub_label_1',
        },
        {
          label: 'intermediary.consumables.bullet.label_2',
          subLabel: 'intermediary.consumables.bullet.sub_label_2',
        },
        {
          label: 'intermediary.consumables.bullet.label_3',
          subLabel: 'intermediary.consumables.bullet.sub_label_3',
        },
      ],
    },
    freeInvoiceLimit: {
      header: 'intermediary.freeInvoiceLimit.header',
      title: 'intermediary.freeInvoiceLimit.title',
      info: 'intermediary.freeInvoiceLimit.info',
      cancel: 'intermediary.freeInvoiceLimit.cancel',
      confirm: 'intermediary.freeInvoiceLimit.confirm',
      referral_bullets: [
        {
          label: 'intermediary.freeInvoiceLimit.bullet.label_1',
          subLabel: 'intermediary.freeInvoiceLimit.bullet.sub_label_1',
        },
        {
          label: 'intermediary.freeInvoiceLimit.bullet.label_2',
          subLabel: 'intermediary.freeInvoiceLimit.bullet.sub_label_2',
        },
        {
          label: 'intermediary.freeInvoiceLimit.bullet.label_3',
          subLabel: 'intermediary.freeInvoiceLimit.bullet.sub_label_3',
        },
      ],
      bullets: [
        {
          label: 'intermediary.freeInvoiceLimit.bullet.label_1',
          subLabel: 'intermediary.freeInvoiceLimit.bullet.sub_label_1',
        },
        {
          label: 'intermediary.freeInvoiceLimit.bullet.label_2',
          subLabel: 'intermediary.freeInvoiceLimit.bullet.sub_label_2',
        },
        {
          label: 'intermediary.freeInvoiceLimit.bullet.label_3',
          subLabel: 'intermediary.freeInvoiceLimit.bullet.sub_label_3',
        },
      ],
    },
  },
}

const headerDiv = (header: string, className: string): JSX.Element => {
  return (
    <div className={`header ${className}-header`}>
      <img src={'/images/premiumFeatureIntermediary/star_with_circle.svg'} className={`${className}-header__img`} />
      <h1 className={`${className}-header__h1`}>{l(header)}</h1>
    </div>
  )
}

const bulletDiv = (bullet, index: number): JSX.Element => {
  return (
    <div key={`bullet_${index}`} className={'bullet'}>
      <img src={'/images/checkmark_green.svg'} className={'bullet__img'} />
      <div className={'bullet-info'}>
        <h1 className={'bullet-info__label'}>{l(bullet.label)}</h1>
        <p className={'bullet-info__sublabel'}>{l(bullet.subLabel)}</p>
      </div>
    </div>
  )
}

const bodyDiv = (intermediary, imgsrc: string, props: IntermediaryConfigProps): JSX.Element => {
  const bulletKey = props.subscription && props.subscription.referralIdUsed ? 'referral_bullets' : 'bullets'
  const allBullets = intermediary[bulletKey] || intermediary.bullets
  const bulletsCopy = [...allBullets]
  const noLastBullet = bulletsCopy.sort((a, b) => a.label.localeCompare(b.label)).splice(0, bulletsCopy.length - 1)
  const isOnFreePlan = props.subscription && props.subscription.status === 'free'
  const bulletObject = isOnFreePlan && allBullets.length - 1 === noLastBullet.length ? noLastBullet : allBullets

  return (
    <div className={`${props.className}-body`}>
      <img src={imgsrc} className={`${props.className}-body__img`} />
      <div className={`${props.className}-body__main`}>
        <h1>{l(intermediary.title)}</h1>
        <p dangerouslySetInnerHTML={{ __html: l(intermediary.info) }} />
        {intermediary.infoLink && (
          <Button
            label={l(intermediary.infoLinkLabel)}
            type={ButtonType.link}
            action={() => window.open(l(intermediary.infoLink), '_blank')}
          />
        )}
      </div>
      <div className={`${props.className}-body__bullets`}>{bulletObject.map(bulletDiv)}</div>
    </div>
  )
}

export const PaperInvoiceIntermediary = (props: IntermediaryConfigProps): IntermediaryConfigInterface => {
  const imgsrc = '/images/premiumFeatureIntermediary/premium_icon_sendbypost.svg'
  return {
    header: headerDiv(localizationKeys.intermediary.paper.header, props.className),
    body: bodyDiv(localizationKeys.intermediary.paper, imgsrc, props),
    footerButtons: {
      cancel: l(localizationKeys.intermediary.paper.cancel),
      confirm: l(localizationKeys.intermediary.paper.confirm),
      automations: {
        cancel: 'cancel-paperIntermediaryModals',
        confirm: 'confirm-paperIntermediaryModals',
      },
      analytics: {
        cancel: {
          name: trackingFields.name.sendPaywall,
          payload: {
            type: trackingFields.type.paywall,
            deliveryType: trackingFields.deliveryType.paper,
            documentType: trackingFields.documentType.invoice,
            documentId: props.context.documentId,
            category: trackingFields.category.document,
            result: trackingFields.result.cancel,
            placement: checkPlacement(window.location.pathname),
          },
        },
        confirm: {
          name: trackingFields.name.sendPaywall,
          payload: {
            type: trackingFields.type.paywall,
            deliveryType: trackingFields.deliveryType.paper,
            documentType: trackingFields.documentType.invoice,
            documentId: props.context.documentId,
            category: trackingFields.category.document,
            result: trackingFields.result.success,
            placement: checkPlacement(window.location.pathname),
          },
        },
      },
    },
  }
}

export const EInvoiceIntermediary = (props: IntermediaryConfigProps): IntermediaryConfigInterface => {
  const imgsrc = '/images/premiumFeatureIntermediary/premium_icon_e-invoice.svg'
  return {
    header: headerDiv(localizationKeys.intermediary.einvoice.header, props.className),
    body: bodyDiv(localizationKeys.intermediary.einvoice, imgsrc, props),
    footerButtons: {
      cancel: l(localizationKeys.intermediary.einvoice.cancel),
      confirm: l(localizationKeys.intermediary.einvoice.confirm),
      automations: {
        cancel: 'cancel-einvoiceIntermediaryModals',
        confirm: 'confirm-einvoiceIntermediaryModals',
      },
      analytics: {
        cancel: {
          name: trackingFields.name.sendPaywall,
          payload: {
            type: trackingFields.type.paywall,
            deliveryType: trackingFields.deliveryType.eInvoice,
            documentId: props.context.documentId,
            category: trackingFields.category.document,
            result: trackingFields.result.cancel,
            placement: checkPlacement(window.location.pathname),
          },
        },
        confirm: {
          name: trackingFields.name.sendPaywall,
          payload: {
            type: trackingFields.type.paywall,
            deliveryType: trackingFields.deliveryType.eInvoice,
            documentId: props.context.documentId,
            category: trackingFields.category.document,
            result: trackingFields.result.success,
            placement: checkPlacement(window.location.pathname),
          },
        },
      },
    },
  }
}

export const ReminderIntermediary = (props: IntermediaryConfigProps): IntermediaryConfigInterface => {
  const imgsrc = '/images/premiumFeatureIntermediary/payment_reminder_intermediary.svg'
  const keys = localizationKeys.intermediary.reminder

  return {
    header: headerDiv(keys.header, props.className),
    body: bodyDiv(keys, imgsrc, props),
    footerButtons: {
      cancel: l(keys.cancel),
      confirm: l(keys.confirm),
      automations: {
        cancel: 'cancel-reminderIntermediaryModals',
        confirm: 'confirm-reminderIntermediaryModals',
      },
      analytics: {
        cancel: {
          name: trackingFields.name.reminderSettingsPaywall,
          payload: {
            type: trackingFields.type.paywall,
            category: trackingFields.category.reminder,
            result: trackingFields.result.cancel,
            placement: checkPlacement(window.location.pathname),
          },
        },
        confirm: {
          name: trackingFields.name.reminderSettingsPaywall,
          payload: {
            type: trackingFields.type.paywall,
            category: trackingFields.category.reminder,
            result: trackingFields.result.success,
            placement: checkPlacement(window.location.pathname),
          },
        },
      },
    },
  }
}
export const ReminderPostIntermediary = (props: IntermediaryConfigProps): IntermediaryConfigInterface => {
  const imgsrc = '/images/premiumFeatureIntermediary/reminder_by_post_intermediary.svg'
  const keys = localizationKeys.intermediary.reminderByPost
  return {
    header: headerDiv(keys.header, props.className),
    body: bodyDiv(keys, imgsrc, props),
    footerButtons: {
      cancel: l(keys.cancel),
      confirm: l(keys.confirm),
      automations: {
        cancel: 'cancel-reminderIntermediaryModals',
        confirm: 'confirm-reminderIntermediaryModals',
      },
      analytics: {
        cancel: {
          name: trackingFields.name.sendPaywall,
          payload: {
            type: trackingFields.type.paywall,
            category: trackingFields.category.reminder,
            result: trackingFields.result.cancel,
            documentType: trackingFields.documentType.reminder,
            deliveryType: trackingFields.deliveryType.paper,
            placement: checkPlacement(window.location.pathname),
            documentId: props.context.documentId,
          },
        },
        confirm: {
          name: trackingFields.name.sendPaywall,
          payload: {
            type: trackingFields.type.paywall,
            category: trackingFields.category.reminder,
            result: trackingFields.result.success,
            documentType: trackingFields.documentType.reminder,
            deliveryType: trackingFields.deliveryType.paper,
            placement: checkPlacement(window.location.pathname),
            documentId: props.context.documentId,
          },
        },
      },
    },
  }
}

export const TrackerIntermediary = (props: IntermediaryConfigProps): IntermediaryConfigInterface => {
  const imgsrc = '/images/premiumFeatureIntermediary/timetracker_intermediary.svg'
  const keys = localizationKeys.intermediary.tracker
  return {
    header: headerDiv(keys.header, props.className),
    body: bodyDiv(keys, imgsrc, props),
    footerButtons: {
      cancel: l(keys.cancel),
      confirm: l(keys.confirm),
      automations: {
        cancel: 'cancel-trackerIntermediaryModals',
        confirm: 'confirm-trackerIntermediaryModals',
      },
      analytics: {
        cancel: {
          name: trackingFields.name.trackerPaywall,
          payload: {
            type: trackingFields.type.paywall,
            category: trackingFields.category.tracker,
            result: trackingFields.result.cancel,
            placement: trackingFields.placement.tracker,
          },
        },
        confirm: {
          name: trackingFields.name.trackerPaywall,
          payload: {
            type: trackingFields.type.paywall,
            category: trackingFields.category.tracker,
            result: trackingFields.result.success,
            placement: trackingFields.placement.tracker,
          },
        },
      },
    },
  }
}

export const TradenamesIntermediary = (props: IntermediaryConfigProps): IntermediaryConfigInterface => {
  const imgsrc = '/images/premiumFeatureIntermediary/tradenames intermediary.svg'
  const keys = localizationKeys.intermediary.tradeNames
  return {
    header: headerDiv(keys.header, props.className),
    body: bodyDiv(keys, imgsrc, props),
    footerButtons: {
      cancel: l(keys.cancel),
      confirm: l(keys.confirm),
      automations: {
        cancel: 'cancel-tradenamesIntermediaryModals',
        confirm: 'confirm-tradenamesIntermediaryModals',
      },
      analytics: {
        cancel: {
          name: trackingFields.name.tradeNamePaywall,
          payload: {
            type: trackingFields.type.paywall,
            category: trackingFields.category.setting,
            result: trackingFields.result.cancel,
            placement: trackingFields.placement.myAccount,
          },
        },
        confirm: {
          name: trackingFields.name.tradeNamePaywall,
          payload: {
            type: trackingFields.type.paywall,
            category: trackingFields.category.setting,
            result: trackingFields.result.success,
            placement: trackingFields.placement.myAccount,
          },
        },
      },
    },
  }
}

export const EstimateToInvoiceIntermediary = (props: IntermediaryConfigProps): IntermediaryConfigInterface => {
  const imgsrc = '/images/premiumFeatureIntermediary/estimate_to_invoice_intermediary.svg'
  const keys = localizationKeys.intermediary.estimateToInvoice
  return {
    header: headerDiv(keys.header, props.className),
    body: bodyDiv(keys, imgsrc, props),
    footerButtons: {
      cancel: l(keys.cancel),
      confirm: l(keys.confirm),
      automations: {
        cancel: 'cancel-estimateToInvoiceIntermediaryModals',
        confirm: 'confirm-estimateToInvoiceIntermediaryModals',
      },
      analytics: {
        cancel: {
          name: trackingFields.name.estimatePaywall,
          payload: {
            type: trackingFields.type.paywall,
            category: trackingFields.category.document,
            result: trackingFields.result.cancel,
            placement: trackingFields.placement.estimates,
            documentType: trackingFields.documentType.estimate,
          },
        },
        confirm: {
          name: trackingFields.name.estimatePaywall,
          payload: {
            type: trackingFields.type.paywall,
            category: trackingFields.category.document,
            result: trackingFields.result.success,
            placement: trackingFields.placement.estimates,
            documentType: trackingFields.documentType.estimate,
          },
        },
      },
    },
  }
}

export const CustomerIntermediary = (props: IntermediaryConfigProps): IntermediaryConfigInterface => {
  const imgsrc = '/images/premiumFeatureIntermediary/customer_intermediary.svg'
  const keys = localizationKeys.intermediary.customer
  return {
    header: headerDiv(keys.header, props.className),
    body: bodyDiv(keys, imgsrc, props),
    footerButtons: {
      cancel: l(keys.cancel),
      confirm: l(keys.confirm),
      automations: {
        cancel: 'cancel-customerIntermediaryModals',
        confirm: 'confirm-customerIntermediaryModals',
      },
      analytics: {
        cancel: {
          name: trackingFields.name.customerLimitPaywall,
          payload: {
            type: trackingFields.type.paywall,
            category: trackingFields.category.customer,
            result: trackingFields.result.cancel,
            placement: checkPlacement(window.location.pathname),
          },
        },
        confirm: {
          name: trackingFields.name.customerLimitPaywall,
          payload: {
            type: trackingFields.type.paywall,
            category: trackingFields.category.customer,
            result: trackingFields.result.success,
            placement: checkPlacement(window.location.pathname),
          },
        },
      },
    },
  }
}

export const PaymentScheduleIntermediary = (props: IntermediaryConfigProps): IntermediaryConfigInterface => {
  const imgsrc = '/images/premiumFeatureIntermediary/payment_schedule_intermediary.svg'
  const keys = localizationKeys.intermediary.payment_schedule
  return {
    header: headerDiv(keys.header, props.className),
    body: bodyDiv(keys, imgsrc, props),
    footerButtons: {
      cancel: l(keys.cancel),
      confirm: l(keys.confirm),
      automations: {
        cancel: 'cancel-paymentScheduleIntermediaryModals',
        confirm: 'confirm-paymentScheduleIntermediaryModals',
      },
      analytics: {
        cancel: {
          name: trackingFields.name.paymentSchedulePaywall,
          payload: {
            type: trackingFields.type.paywall,
            category: trackingFields.category.paymentSchedule,
            result: trackingFields.result.cancel,
            placement: trackingFields.placement.editor,
          },
        },
        confirm: {
          name: trackingFields.name.paymentSchedulePaywall,
          payload: {
            type: trackingFields.type.paywall,
            category: trackingFields.category.paymentSchedule,
            result: trackingFields.result.success,
            placement: trackingFields.placement.editor,
          },
        },
      },
    },
  }
}

export const RecurringProfileIntermediary = (props: IntermediaryConfigProps): IntermediaryConfigInterface => {
  const imgsrc = '/images/premiumFeatureIntermediary/recurring_profile_intermediary.svg'
  const keys = localizationKeys.intermediary.recurring_profile
  return {
    header: headerDiv(keys.header, props.className),
    body: bodyDiv(keys, imgsrc, props),
    footerButtons: {
      cancel: l(keys.cancel),
      confirm: l(keys.confirm),
      automations: {
        cancel: 'cancel-recurringProfileIntermediaryModals',
        confirm: 'confirm-recurringProfileIntermediaryModals',
      },
      analytics: {
        cancel: {
          name: trackingFields.name.recurringInvoicePaywall,
          payload: {
            type: trackingFields.type.paywall,
            category: trackingFields.category.recurringInvoice,
            result: trackingFields.result.cancel,
            placement: trackingFields.placement.editor,
          },
        },
        confirm: {
          name: trackingFields.name.recurringInvoicePaywall,
          payload: {
            type: trackingFields.type.paywall,
            category: trackingFields.category.recurringInvoice,
            result: trackingFields.result.success,
            placement: trackingFields.placement.editor,
          },
        },
      },
    },
  }
}

export const BankingIntermediary = (props: IntermediaryConfigProps): IntermediaryConfigInterface => {
  const imgsrc = '/images/premiumFeatureIntermediary/banking_intermediary.svg'
  const keys = localizationKeys.intermediary.banking
  return {
    header: headerDiv(keys.header, props.className),
    body: bodyDiv(keys, imgsrc, props),
    footerButtons: {
      cancel: l(keys.cancel),
      confirm: l(keys.confirm),
      automations: {
        cancel: 'cancel-bankingIntermediaryModals',
        confirm: 'confirm-bankingIntermediaryModals',
      },
      analytics: {
        cancel: {
          name: trackingFields.name.bankingPaywall,
          payload: {
            type: trackingFields.type.paywall,
            category: trackingFields.category.banking,
            result: trackingFields.result.cancel,
            placement: trackingFields.placement.editor,
          },
        },
        confirm: {
          name: trackingFields.name.bankingPaywall,
          payload: {
            type: trackingFields.type.paywall,
            category: trackingFields.category.banking,
            result: trackingFields.result.success,
            placement: trackingFields.placement.editor,
          },
        },
      },
    },
  }
}

export const EmailAttachmentIntermediary = (props: IntermediaryConfigProps): IntermediaryConfigInterface => {
  const imgsrc = '/images/premiumFeatureIntermediary/email_attachment_intermediary.svg'
  const keys = localizationKeys.intermediary.emailAttachment
  return {
    header: headerDiv(keys.header, props.className),
    body: bodyDiv(keys, imgsrc, props),
    footerButtons: {
      cancel: l(keys.cancel),
      confirm: l(keys.confirm),
      automations: {
        cancel: 'cancel-emailAttachmentIntermediaryModals',
        confirm: 'confirm-emailAttachmentIntermediaryModals',
      },
      analytics: {
        cancel: {
          name: trackingFields.name.sendPaywall,
          payload: {
            type: trackingFields.type.paywall,
            category: trackingFields.category.reminder,
            result: trackingFields.result.cancel,
            documentType: trackingFields.documentType.reminder,
            placement: trackingFields.placement.invoices,
            deliveryType: trackingFields.deliveryType.email,
            documentId: props.context.documentId,
          },
        },
        confirm: {
          name: trackingFields.name.sendPaywall,
          payload: {
            type: trackingFields.type.paywall,
            category: trackingFields.category.reminder,
            result: trackingFields.result.success,
            documentType: trackingFields.documentType.reminder,
            placement: trackingFields.placement.invoices,
            deliveryType: trackingFields.deliveryType.email,
            documentId: props.context.documentId,
          },
        },
      },
    },
  }
}

export const DocumentCustomisationIntermediary = (props: IntermediaryConfigProps): IntermediaryConfigInterface => {
  const imgsrc = '/images/premiumFeatureIntermediary/customise_invoice_intermediary.svg'
  const keys = localizationKeys.intermediary.documentCustomisation
  return {
    header: headerDiv(keys.header, props.className),
    body: bodyDiv(keys, imgsrc, props),
    footerButtons: {
      cancel: l(keys.cancel),
      confirm: l(keys.confirm),
      automations: {
        cancel: 'cancel-documentCustomisationIntermediaryModals',
        confirm: 'confirm-documentCustomisationIntermediaryModals',
      },
      analytics: {
        cancel: {
          name: trackingFields.name.bankingPaywall,
          payload: {
            type: trackingFields.type.paywall,
            category: trackingFields.category.banking,
            result: trackingFields.result.cancel,
            placement: trackingFields.placement.editor,
          },
        },
        confirm: {
          name: trackingFields.name.bankingPaywall,
          payload: {
            type: trackingFields.type.paywall,
            category: trackingFields.category.banking,
            result: trackingFields.result.success,
            placement: trackingFields.placement.editor,
          },
        },
      },
    },
  }
}

export const ConsumablesIntermediary = (props: IntermediaryConfigProps): IntermediaryConfigInterface => {
  const imgsrc = '/images/premiumFeatureIntermediary/consumables_intermediary.svg'
  const keys = localizationKeys.intermediary.consumables
  return {
    header: headerDiv(keys.header, props.className),
    body: bodyDiv(keys, imgsrc, props),
    footerButtons: {
      cancel: l(keys.cancel),
      confirm: l(keys.confirm),
      automations: {
        cancel: 'cancel-consumablesIntermediaryModals',
        confirm: 'confirm-consumablesIntermediaryModals',
      },
      analytics: {
        cancel: {
          name: trackingFields.name.consumablesPaywall,
          payload: {
            type: trackingFields.type.paywall,
            category: trackingFields.category.consumables,
            result: trackingFields.result.cancel,
            placement: trackingFields.placement.myAccount,
          },
        },
        confirm: {
          name: trackingFields.name.consumablesPaywall,
          payload: {
            type: trackingFields.type.paywall,
            category: trackingFields.category.consumables,
            result: trackingFields.result.success,
            placement: trackingFields.placement.myAccount,
          },
        },
      },
    },
  }
}

export const FreeInvoiceLimitIntermediary = (props: IntermediaryConfigProps): IntermediaryConfigInterface => {
  const imgsrc = '/images/premiumFeatureIntermediary/free_invoice_limit_intermediary.svg'
  const keys = localizationKeys.intermediary.freeInvoiceLimit
  return {
    header: headerDiv(keys.header, props.className),
    body: bodyDiv(keys, imgsrc, props),
    footerButtons: {
      cancel: l(keys.cancel),
      confirm: l(keys.confirm),
      automations: {
        cancel: 'cancel-freeInvoiceLimitIntermediaryModals',
        confirm: 'confirm-freeInvoiceLimitIntermediaryModals',
      },
      analytics: {
        cancel: {
          name: trackingFields.name.freeInvoiceLimit,
          payload: {
            type: trackingFields.type.paywall,
            category: trackingFields.category.document,
            result: trackingFields.result.cancel,
            placement: checkPlacement(window.location.pathname),
          },
        },
        confirm: {
          name: trackingFields.name.freeInvoiceLimit,
          payload: {
            type: trackingFields.type.paywall,
            category: trackingFields.category.document,
            result: trackingFields.result.success,
            placement: checkPlacement(window.location.pathname),
          },
        },
      },
    },
  }
}
